import { Link } from 'react-router-dom';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAY_LABELS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const STATUS_COLORS = {
  draft: 'bg-gray-200 text-gray-700',
  active: 'bg-blue-200 text-blue-800',
  completed: 'bg-green-200 text-green-800',
};

/**
 * Weekly calendar view for delivery plans.
 * Desktop: 7-column grid. Mobile: vertical list by day.
 * @param {{ plans: object[], weekStart: string }} props
 */
export default function PlanCalendar({ plans, weekStart }) {
  const startDate = getMonday(weekStart ? new Date(weekStart) : new Date());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    return d;
  });

  const plansByDate = groupByDate(plans);

  return (
    <>
      {/* Desktop: grid 7 columns */}
      <div className="hidden md:grid grid-cols-7 gap-1 text-xs">
        {DAY_LABELS.map((label) => (
          <div key={label} className="text-center font-semibold text-gray-500 py-1">{label}</div>
        ))}
        {days.map((day) => {
          const dateKey = toDateStr(day);
          const dayPlans = plansByDate[dateKey] || [];
          const isToday = dateKey === toDateStr(new Date());
          return (
            <div key={dateKey}
              className={`min-h-[80px] border rounded p-1 ${isToday ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
              <div className="text-right text-gray-400 mb-1">{day.getDate()}</div>
              {dayPlans.map((plan) => (
                <Link key={plan.id} to={`/plans/${plan.id}`}
                  className={`block rounded px-1 py-0.5 mb-0.5 truncate ${STATUS_COLORS[plan.status] || 'bg-gray-100'}`}>
                  {plan.driver_name} ({plan.stop_count})
                </Link>
              ))}
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical list */}
      <div className="md:hidden space-y-2">
        {days.map((day, i) => {
          const dateKey = toDateStr(day);
          const dayPlans = plansByDate[dateKey] || [];
          const isToday = dateKey === toDateStr(new Date());
          return (
            <div key={dateKey}
              className={`rounded-xl p-3 ${isToday ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-100'}`}>
              <div className="flex justify-between items-center mb-1.5">
                <span className={`text-sm font-semibold ${isToday ? 'text-blue-700' : 'text-[#0f172a]'}`}>
                  {DAY_LABELS_FULL[i]} {day.getDate()}
                </span>
                {isToday && <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full font-medium">Aujourd'hui</span>}
              </div>
              {dayPlans.length > 0 ? dayPlans.map((plan) => (
                <Link key={plan.id} to={`/plans/${plan.id}`}
                  className={`block rounded-lg px-3 py-2 mb-1 text-sm ${STATUS_COLORS[plan.status] || 'bg-gray-100'}`}>
                  {plan.driver_name} — {plan.stop_count} arrêts
                </Link>
              )) : (
                <p className="text-xs text-gray-400">Aucune tournée</p>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toDateStr(d) {
  return d.toISOString().split('T')[0];
}

function groupByDate(plans) {
  const map = {};
  for (const plan of plans) {
    const key = plan.date ? plan.date.split('T')[0] : '';
    if (!key) continue;
    if (!map[key]) map[key] = [];
    map[key].push(plan);
  }
  return map;
}
